import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { PencilSearchableDropdownComponent } from 'src/app/standalones/components/pencil-dropdown/pencil-searchable-dropdown.component';

/**
 * Generic option interface for dropdown items.
 */
export interface DropdownOption<T = any> {
  value: T;
  label: string;
  subtitle?: string;
  disabled?: boolean;
}

/**
 * Generic component for displaying a settings dropdown with title, description, and options.
 * Accepts options as input array and handles selection with disabled item support.
 *
 * @template T - The type of the option value
 */
@Component({
  selector: 'app-user-settings-permissions-dropdown',
  templateUrl: './user-settings-permissions-dropdown.component.html',
  styleUrls: ['./user-settings-permissions-dropdown.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, PencilSearchableDropdownComponent],
})
export class UserSettingsPermissionsDropdownComponent<T = any> implements OnChanges {
  @Input() title: string = '';
  @Input() description: string = '';
  @Input() selectedValue: string = '';
  @Input() options!: DropdownOption<T>[];
  @Input() bindValue: string = 'value';
  @Input() bindLabel: string = 'label';
  @Input() placeholder: string = 'Select an option';

  @Output() optionSelected = new EventEmitter<T>();

  selectedItem$: BehaviorSubject<DropdownOption<T> | null> =
    new BehaviorSubject<DropdownOption<T> | null>(null);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['selectedValue']) {
      this.computeSelectedItem();
    }
  }

  private computeSelectedItem(): void {
    const options = this.options || [];
    if (!options || options.length === 0) {
      this.selectedItem$.next(null);
      return;
    }
    const selectedItem = options.find((option) => option.label === this.selectedValue) || null;
    this.selectedItem$.next(selectedItem);
  }

  /**
   * Handles item selection from the dropdown.
   * Prevents selection of disabled items.
   */
  onItemSelected(option: DropdownOption<T>): void {
    // Prevent selection of disabled items
    if (option.disabled) {
      return;
    }
    this.optionSelected.emit(option.value);
  }
}
